import React, {useCallback, useMemo, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import {TitledBody} from '../../common/components/TitledBody'
import {ServerPage, VpUser} from '../../app/types'
import {useSelector} from 'react-redux'
import {api, buildPaginatedUrl} from '../../common/api'
import ErrorAlert from '../../common/components/ErrorAlert'
import {AddButton} from '../../common/components/AddButton'
import {SearchField} from '../../common/components/SearchField'
import {Sort, useInfiniteScroll} from '../../common/components/table/useInfiniteScroll'
import {ColumnDefinition, InfiniteScrollTable} from '../../common/components/table/Table'
import TableRowActionsMenu from '../../common/components/table/TableRowActionsMenu'
import {sorterHeader} from '../../common/components/table/SorterHeader'
import DeletionModal from '../../common/components/DeletionModal'
import {Dialog, DialogActions, DialogContent, DialogTitle, Grid} from '@mui/material'
import {useAccessHelpers, useUrlHelpers} from "../../common/util/hooks";
import {customerOriginIdSelector} from "../../authentication/redux";
import CancelButton from "../../common/form/CancelButton";
import useVpUserForm from "./useVpUserForm";
import CheckIcon from "@mui/icons-material/Check";

export const UserManagement = () => {
    const { t } = useTranslation()
    const customerOriginId = useSelector(customerOriginIdSelector)
    const [entryToDelete, setEntryToDelete] = useState<VpUser | null>(null)
    const [entryToUpdate, setEntryToUpdate] = useState<VpUser | null>(null)
    const {createEnterpriseUrl} = useUrlHelpers();
    const {hasWriteAccess, onlyWithWriteAccess} = useAccessHelpers()

    const fetchUsers = useCallback(
        async (pageParam = 0, sortParam: Sort, searchTermParam: string | null): Promise<ServerPage<VpUser>> => {
            const { data } = await api.get(buildPaginatedUrl(createEnterpriseUrl('vpUsers'), pageParam, sortParam, searchTermParam))
            return data
        },
        [createEnterpriseUrl]
    )

    const { sort, setSort, setSearchTerm, rows, error, fetchNextPage, hasNextPage, isFetching, refetch } = useInfiniteScroll<VpUser>({
        queryKey: `user-management-${customerOriginId || 'n/a'}`,
        initialSort: { field: 'lastName', order: 'asc' },
        fetchFunction: fetchUsers,
    })

    const onSubmit = useCallback(
        async (formData: VpUser) => {
            const url = createEnterpriseUrl(`vpUsers${entryToUpdate?.id ? `/${entryToUpdate.id}` : ''}`)
            if (entryToUpdate?.id) {
                await api.put(url, formData)
            } else {
                await api.post(url, formData)
            }

            setEntryToUpdate(null)

            await refetch()
        },
        [refetch, setEntryToUpdate, entryToUpdate, createEnterpriseUrl]
    )

    const { handleSubmit, form: Form, submitButton: SubmitButton, reset, setValue } = useVpUserForm({ entry: entryToUpdate, onSubmit })

    const rowActions = useMemo(
        () => [
            {
                key: 'edit',
                text: t('general.edit'),
                onClick: async (record: VpUser) => {
                    reset()
                    setEntryToUpdate(record)
                    setValue('lastName', record.lastName)
                    setValue('firstName', record.firstName)
                    setValue('email', record.email)
                    setValue('enterpriseAdmin', record.enterpriseAdmin)
                    return false
                },
                dividerBeneath: true,
                needsWriteAccess: true
            },
            {
                key: 'delete',
                text: t('general.delete'),
                onClick: (record: VpUser) => {
                    setEntryToDelete(record)
                },
                needsWriteAccess: true
            },
        ],
        [t, setValue, reset]
    )

    // @ts-ignore
    const columns: ColumnDefinition<VpUser>[] = useMemo(
        () => [
            {
                title: t('users.table.name'),
                key: 'lastName',
                sorter: true,
                render: (text: string, record: VpUser) => <>{`${record.lastName} ${record.firstName}`}</>,
            },
            {
                title: t('users.table.e-mail'),
                key: 'email',
                sorter: true,
                render: (text: string) => <>{text}</>,
            },
            {
                title: t('user-management.enterprise-administrator'),
                key: 'enterpriseAdmin',
                sorter: true,
                render: (admin: boolean) => <>{admin ? <CheckIcon color={'success'}/> : ''}</>,
            },
            {
                title: '',
                key: 'uuid',
                sorter: false,
                render: (text: string, record: VpUser) => <TableRowActionsMenu record={record} menuItems={rowActions} readOnly={!hasWriteAccess}/>,

            }
        ],
        [t, rowActions, hasWriteAccess]
    )

    const onSearch = useCallback(async (searchTerm: string) => setSearchTerm(searchTerm), [setSearchTerm])

    const sorter = useCallback(col => sorterHeader(col, sort, setSort), [sort, setSort])

    const getRowKey = useCallback(entry => entry.uuid, [])

    const getDeleteUrl = useCallback(i => i ? createEnterpriseUrl(`vpUsers/${i.id}`) : null, [createEnterpriseUrl])

    const buttonPanel = useMemo(
        () => onlyWithWriteAccess((
            <Grid container item justifyContent={'space-between'} flexDirection={'row'} spacing={2} flexWrap={'nowrap'}>
                {<Grid item>
                        <AddButton
                            onClick={() => {
                                reset()
                                setEntryToUpdate({ lastName: '', firstName: '', email: '', id: '', enterpriseAdmin: true })
                                return null
                            }}
                            label={`${t('user-management.create-new')}`}
                        />
                    </Grid>
                }
            </Grid>)
        ),
        [t, reset, onlyWithWriteAccess]
    )

    const entryForm = useMemo(
        () => (
            <form className={'ant-form ant-form-vertical'} onSubmit={handleSubmit} noValidate={true}>
                <Dialog disablePortal open={!!entryToUpdate} onClose={() => setEntryToUpdate(null)}>
                    <DialogTitle>{t(entryToUpdate?.id ? 'user-management.update-dialog-title' : 'user-management.create-new')}</DialogTitle>
                    <DialogContent>
                        <Form />
                    </DialogContent>
                    <DialogActions>
                        <SubmitButton />
                        <CancelButton onClick={() => setEntryToUpdate(null)} />
                    </DialogActions>
                </Dialog>
            </form>
        ),
        [Form, SubmitButton, entryToUpdate, handleSubmit, t]
    )

    return (
        <>
            <TitledBody title={t('user-management.title')} buttonsPanel={buttonPanel}>
                <ErrorAlert showAlert={true} severity={'warning'} body={<Trans i18nKey="user-management.warning-not-in-use" />}/>
                <SearchField onSearch={onSearch} />
                <ErrorAlert showAlert={!!error} />
                <InfiniteScrollTable<VpUser>
                    hasNextPage={hasNextPage}
                    data={rows}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    getRowKey={getRowKey}
                    sorter={sorter}
                    columns={columns}
                    tableId={'userMamagementTable'}
                />
                <DeletionModal<VpUser | null>
                    nameToDelete={user => `${user?.lastName} ${user?.firstName}`}
                    itemToDelete={entryToDelete} deleteUrl={getDeleteUrl} onClose={() => setEntryToDelete(null)} onSuccess={refetch} />
                {entryForm}
            </TitledBody>
        </>
    )
}
